import { Component, inject, OnDestroy, OnInit } from '@angular/core';

import { Router, RouterModule } from '@angular/router';
import { AppMenuitem } from './app.menuitem';
import { LayoutService } from '../service/layout.service';
import { combineLatest, Subscription } from 'rxjs';
import { UserService } from '@/services';
import { PermissionValidationService } from '@/services/permission-validation.service';
import { AppStateService } from '@/services/app-state.service';
import { SkeletonModule } from 'primeng/skeleton';
import { MenuItem } from '../models/menu-item.model';
import { PermissionSet } from '@zagops/api/sdk-angular';

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [AppMenuitem, RouterModule, SkeletonModule],
  providers: [Router],
  template: `<ul class="layout-menu">
    @if (!routes?.length && !hasUser) {
      <p-skeleton height="12rem" styleClass="my-12" />
    } @else {
      @for (item of routes; track item; let i = $index) {
        @if (!item.separator) {
          <li app-menuitem [item]="item" [index]="i" [root]="true"></li>
        }
        @if (item.separator) {
          <li class="menu-separator"></li>
        }
      }
    }
  </ul>`,
})
export class AppMenu implements OnInit, OnDestroy {
  layoutService: LayoutService = inject(LayoutService);

  public routes?: MenuItem[] = [
    {
      label: 'ZIP',
      icon: 'pi pi-home',
      items: [
        {
          label: 'App Launcher',
          icon: 'pi pi-fw pi-th-large',
          routerLink: ['/universal-remote/apps'],
          requiredPermissions: [PermissionSet.name.USE_APP_LAUNCHER],
        },
        {
          label: 'Vault',
          icon: 'pi pi-fw pi-key',
          routerLink: ['/universal-remote/vault'],
          requiredPermissions: [PermissionSet.name.USE_PASSWORD_VAULT],
          disabledBadge: {},
        },
      ],
    },
    {
      label: 'ZAP',
      icon: 'pi pi-home',
      items: [
        {
          label: 'Tip Disbursements',
          icon: 'pi pi-fw pi-credit-card',
          routerLink: ['/employees/tip-disbursement'],
          requiredPermissions: [PermissionSet.name.COLLECT_TIP_DISBURSEMENTS],
          disabledBadge: {},
        },
        {
          label: 'Tip Configurations',
          icon: 'pi pi-fw pi-cog',
          routerLink: ['/tipout/tip-configs'],
          requiredPermissions: [PermissionSet.name.CONFIGURE_TIP_DISTRIBUTIONS],
          disabledBadge: {},
        },
        {
          label: 'Tip Rules',
          icon: 'pi pi-fw pi-list-check',
          routerLink: ['/tipout/tip-rules'],
          requiredPermissions: [PermissionSet.name.CONFIGURE_TIP_DISTRIBUTIONS],
          disabledBadge: {},
        },
        {
          label: 'Pay Periods',
          icon: 'pi pi-fw pi-calendar-clock',
          routerLink: ['/tipout/pay-periods'],
          requiredPermissions: [PermissionSet.name.CONFIGURE_PAY_PERIODS],
          disabledBadge: {},
        },
        // {
        //   label: 'Time Entries',
        //   icon: 'pi pi-fw pi-clock',
        //   routerLink: ['/tipout/time-entries'],
        // },
        // {
        //   label: 'AI Analytics',
        //   icon: 'pi pi-fw pi-chart-line',
        //   routerLink: ['/reports'],
        // },
      ],
    },
    {
      label: 'Company Management',
      icon: 'pi pi-fw pi-cog',
      items: [
        {
          label: 'Access',
          icon: 'pi pi-fw pi-lock-open',
          routerLink: ['/management/permission-assignment'],
          requiredPermissions: [
            PermissionSet.name.MANAGE_USER_PERMISSION_ASSIGNMENTS,
          ],
        },
        {
          label: 'Add Members',
          icon: 'pi pi-fw pi-user-plus',
          routerLink: ['/management/invite-users'],
          requiredPermissions: [
            PermissionSet.name.INVITE_USERS_TO_ORGANIZATION,
          ],
        },
        {
          label: 'Manage Members',
          icon: 'pi pi-fw pi-users',
          routerLink: ['/management/org-members'],
          requiredPermissions: [
            PermissionSet.name.MANAGE_USER_PERMISSION_ASSIGNMENTS,
          ],
        },
        {
          label: 'Roles',
          icon: 'pi pi-fw pi-id-card',
          routerLink: ['/management/roles'],
          // Must match the route's gate, or the link shows to people it bounces.
          anyOfPermissions: [
            PermissionSet.name.CONFIGURE_ROLE_ASSIGNMENTS,
            PermissionSet.name.CONFIGURE_ROLES,
          ],
        },
        {
          label: 'Manage Pay Periods',
          icon: 'pi pi-fw pi-calendar',
          routerLink: ['/management/pay-period-management'],
          requiredPermissions: [PermissionSet.name.CONFIGURE_PAY_PERIODS],
          disabledBadge: {},
        },
        {
          label: 'Manage Themes',
          icon: 'pi pi-fw pi-palette',
          routerLink: ['/management/account/themes'],
          requiredPermissions: [PermissionSet.name.MANAGE_ACCOUNT_SETTINGS],
          disabledBadge: {},
        },
      ],
    },
    {
      label: 'ZagOps Internal Admin',
      items: [
        {
          label: 'Customer Onboarding',
          icon: 'pi pi-file-import',
          routerLink: ['/admin/onboarding'],
          requiredPermissions: [
            PermissionSet.name.ZAGOPS_INTERNAL_CUSTOMER_ONBOARDING,
          ],
          hideIfForbidden: true,
        },
        {
          label: 'Customer Contracts',
          icon: 'pi pi-receipt',
          routerLink: ['/admin/contract-management'],
          requiredPermissions: [
            PermissionSet.name.ZAGOPS_INTERNAL_CONTRACT_MANAGEMENT,
          ],
        },
        {
          label: 'Feature Flags',
          icon: 'pi pi-flag',
          routerLink: ['/admin/feature-flags'],
          requiredPermissions: [
            PermissionSet.name.ZAGOPS_INTERNAL_MANAGE_FEATURE_FLAGS,
          ],
          hideIfForbidden: true,
        },
      ],
      hideIfNoChildrenVisible: true,
      hideIfForbidden: true,
    },
  ];

  constructor(
    public router: Router,
    private userService: UserService,
    private permissionValidationService: PermissionValidationService,
    private appStateService: AppStateService,
  ) {}

  private subs: Subscription[] = [];
  public hasUser = false;

  ngOnInit(): void {
    this.subs.push(
      combineLatest([
        this.userService.appUser$,
        this.appStateService.selectedAccountScope$,
      ]).subscribe(([appUser]) => {
        this.hasUser = appUser.user ? true : false;
        if (this.hasUser) {
          try {
            this.updateMenuPermissions();
          } catch (err) {
            console.error('Failed to update menu permissions', err);
          }
        }
      }),
    );
  }

  private updateMenuPermissions(): void {
    this.routes = this.routes?.map((route) =>
      this.checkRoutePermissions(route),
    );
  }

  private checkRoutePermissions(route: MenuItem): MenuItem {
    const updatedRoute = { ...route };

    if (route.requiredPermissions?.length || route.anyOfPermissions?.length) {
      try {
        const hasAccess = route.anyOfPermissions?.length
          ? this.permissionValidationService.hasAnyPermission(
              route.anyOfPermissions,
            )
          : this.permissionValidationService.hasPermissions(
              route.requiredPermissions ?? [],
            );
        updatedRoute.disabled = !hasAccess;
      } catch (err) {
        console.error('Failed to check route permissions', {
          error: err,
          routeLabel: route.label,
        });
        updatedRoute.disabled = true;
      }
    }

    if (route.items?.length) {
      updatedRoute.items = route.items.map((item) => {
        try {
          return this.checkRoutePermissions(item);
        } catch (err) {
          console.error('Failed to process menu item', {
            error: err,
            itemLabel: item.label,
            parentLabel: route.label,
          });
          return { ...item, disabled: true };
        }
      });

      if (
        updatedRoute.hideIfForbidden === true &&
        updatedRoute.disabled !== true &&
        updatedRoute.items.every(
          (item) => item.disabled === true || item.visible === false,
        )
      ) {
        updatedRoute.disabled = true;
      }
    }

    if (updatedRoute.hideIfForbidden) {
      updatedRoute.visible = !updatedRoute.disabled;
    }

    if (route.hideIfNoChildrenVisible && route.items?.length) {
      const hasVisibleChild = updatedRoute.items?.some(
        (item: MenuItem) => item.visible !== false,
      );
      updatedRoute.visible = hasVisibleChild;
    }
    return updatedRoute;
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
